import { BaseService } from "./BaseService";
import { AxiosResponse } from "axios";
import ApiResponse from "../Models/ApiResponse";
import api from "../Interceptors/Interceptor";
import ReferenceModel from "../Models/ReferenceModel";
import { Page } from "../Models/Page";

export interface ICrudDashboardModel {
	uuid?: string
	dashboardConditions: IdashboardConditionModel[]
	icon: string
	name: string
	description: string
	sequence: number;
	requiredPermission: string
	active: boolean
	customer: ReferenceModel
}

export interface IDashboardModel {
	uuid: string
	type: ReferenceModel
	dashboardType: ReferenceModel
	/** Category folder / grouping in dashboard management; optional on summary payloads. */
	dashboardCategory?: ReferenceModel
	dashboardItems: IDashboardItemModel[]
	dashboardConditions: IdashboardConditionModel[]
	icon: string
	name: string
	description: string
	sequence: number;
	requiredPermission: string
	active: boolean
}

export interface IDashboardItemModel {
	uuid: string
	metric: ReferenceModel
	graphType: ReferenceModel
	itemProperty: IDashlyDashboardItemPropertyModel;
	itemType: ReferenceModel;
	limits: IDashlyDashboardItemLimitModel[]
	x: number
	y: number
	width: number
	height: number
	sequence: number
	active: boolean
}

export interface IdashboardConditionModel {
	uuid: string
	parameter: string
	field: string
	value: string;
	active: boolean
}

export interface IDashboardMenuItemModel {
	uuid: string
	icon: string
	name: string
	requiredPermission: string
	active: boolean
}

export interface IDashboardViewerCategoryModel {
	uuid?: string
	name?: string
	icon?: string
	sequence?: number
}

export interface IDashboardViewerItemModel {
	uuid: string
	hasThumbnail?: boolean
	thumbnail?: IDashboardThumbnailModel
	category?: IDashboardViewerCategoryModel
	favourite: boolean
	customer: ReferenceModel
	icon: string
	name: string
	updatedDate?: string
}

export interface IDashboardThumbnailModel {
	createdDate: string
	thumbnail: string
}

export interface ISpoofyDashboardMenuItemModel {
	uuid: string
	icon: string
	name: string
	dashboards: ISpoofyDashboardMenuItemModel[]
}

export interface IDashlyDashboardModel {
	uuid: string
	dashboardType: ReferenceModel
	dashboardItems: IDashlyDashboardItemModel[]
	deleteItems?: IDashlyDashboardItemModel[]
	filters?: IDashlyFilterModel[]
	filterOptions?: IDashlyDashboardFilterOptionModel[]
	forcedFilters?: IDashlyDashboardForcedFilterModel[]
	icon: string
	name: string
	description: string
	sequence: number;
	requiredPermission: string
	favourite: boolean
	active: boolean
	customer: any
	updateThumbnail: boolean
}

export interface IDashlyDashboardFilterOptionModel {
	parameter: string
	sequence: number
}

export interface IDashlyFilterModelContainer {
	filters: IDashlyFilterModel[];
}

export interface IDashlyFilterModel {
	uuid?: string
	options?: IDashlyFilterOptionModel[]
	expiration?: ReferenceModel
	inputType?: string
	parameter: string
	sequence?: number
	defaultOption?: IDashlyFilterOptionModel
	defaultValue?: string
	offSetValue?: number
	defaultSelection: boolean
	dynamic: boolean
	loadingFailed?: boolean
	smart: boolean
	active?: boolean
	deleted?: boolean

	[key: string]: any;
}

export interface IDashlyFilterOptionModel {
	translationName: string
	parameter: string
	value?: string
	[key: string]: any;
}

export function translateFilterOptionLabel(
	translationName: string | undefined,
	t: (key: string) => string,
	i18n: { exists: (key: string) => boolean },
): string {
	if (!translationName) {
		return translationName ?? "";
	}
	return i18n.exists(translationName) ? t(translationName) : translationName;
}

export function translateDashlyFilterOptionLabels(
	filters: IDashlyFilterModel[] | null | undefined,
	t: (key: string) => string,
	i18n: { exists: (key: string) => boolean },
): IDashlyFilterModel[] {
	if (!filters?.length) {
		return filters ?? [];
	}
	return filters.map((filter) => ({
		...filter,
		options: filter.options?.map((option) => ({
			...option,
			translationName: translateFilterOptionLabel(option.translationName, t, i18n),
		})),
		defaultOption: filter.defaultOption
			? {
				...filter.defaultOption,
				translationName: translateFilterOptionLabel(filter.defaultOption.translationName, t, i18n),
			}
			: filter.defaultOption,
	}));
}

export const COLUMN_TOTAL_POSITION_TOP = "top";
export const COLUMN_TOTAL_POSITION_BOTTOM = "bottom";
export const COLUMN_TOTAL_POSITION_BOTH = "both";

export interface IDashlyDashboardItemPropertyModel {
	icon: string
	iconColor: string
	formattedValue: boolean
	dynamicXAxis: boolean
	dynamicScaling: boolean
	drillthrough: boolean
	drilldown: boolean
	lowerDeltaPositive: boolean
	decimal: boolean
	chartLine: boolean
	disableSort: boolean
	wideFirstColumn: boolean
	showColumnTotals: boolean
	columnTotalPosition: string
	percentage: boolean
	percentagePerMainGrouping: boolean
	rankingAscending: boolean
	rankingDescending: boolean
	chartSwap: boolean
	defaultStatusOption: string
	trendLine: boolean
	visualGroup?: string[]
	sensitiveFieldUuids?: string[]
	visualGroupMode?: string
	ranking?: any
}

export interface IDashlyItemContentModel {
	uuid: string
	value: string
}

export interface IDashlyDashboardItemModel {
	uuid: string
	placeholderUuid: string
	metric: IDashlyDashboardMetricModel
	graphType: ReferenceModel
	itemProperty: IDashlyDashboardItemPropertyModel;
	itemType: ReferenceModel;
	itemContent: IDashlyItemContentModel;
	limits: IDashlyDashboardItemLimitModel[]
	chartNumber?: number;
	kpi?: number;
	name?: string
	x: number
	y: number
	width: number
	height: number
	sequence: number
	icon: string
	iconColor: string
	formattedValue: boolean
	chartLine: boolean
	drillthrough: boolean
	active: boolean
	deleted: boolean
}

export interface IDashlyDashboardMetricModel {
	uuid: string;
	name: string;
	code: string;
	filters: IDashlyFilterModel[];
	compareMetric: boolean;
}

export interface IDashlyDashboardLimitModel {
	uuid: string | null;
	id?: string;
	name: string;
	code: string;
	color?: string;
}

export interface IDashlyDashboardItemLimitModel {
	uuid?: string;
	dashboardItem?: ReferenceModel;
	dashboardItemLimitType?: ReferenceModel;
	dashboardItemLimitAnnotationStyle?: IDashlyDashboardLimitModel;
	limitValue?: number;
	limitValueEnd?: number;
	label?: string;
	deleted?: boolean;

	[key: string]: any;
}

export interface IDashlyDashboardForcedFilterModel {
	uuid?: string;
	metricViewUuid: string;
	dashboard?: ReferenceModel;
	parameter: string;
	sqlOperator?: string;
	values?: string[];
	dataType?: string;
	options: IDashlyFilterOptionModel[];
	incomplete?: boolean;
	active: boolean;
}

export interface IDashboardLayoutPreviewItemModel {
	uuid: string;
	name?: string;
	x: number;
	y: number;
	width: number;
	height: number;
	itemType?: ReferenceModel;
	graphType?: ReferenceModel;
	metric?: { uuid: string; compareMetric?: boolean };
	itemProperty?: IDashlyDashboardItemPropertyModel;
	itemContent?: IDashlyItemContentModel;
	icon?: string;
	iconColor?: string;
}

export interface IDashboardLayoutPreviewFilterModel {
	uuid?: string;
	parameter: string;
	sequence?: number;
	inputType?: string;
	dynamic: boolean;
	smart: boolean;
}

export interface IDashboardLayoutPreviewModel {
	uuid: string;
	name: string;
	dashboardItems: IDashboardLayoutPreviewItemModel[];
	filters: IDashboardLayoutPreviewFilterModel[];
}

export function mapPreviewFilterToDashlyFilter(filter: IDashboardLayoutPreviewFilterModel): IDashlyFilterModel {
	return {
		uuid: filter.uuid,
		parameter: filter.parameter,
		sequence: filter.sequence,
		inputType: filter.inputType,
		dynamic: filter.dynamic,
		smart: filter.smart,
		defaultSelection: false,
	};
}

export function mapPreviewToDashboardModel(preview: IDashboardLayoutPreviewModel | null): IDashlyDashboardModel | null {
	if (!preview) {
		return null;
	}

	return {
		uuid: preview.uuid,
		name: preview.name,
		dashboardType: undefined as any,
		dashboardItems: (preview.dashboardItems ?? []).map((item) => ({
			uuid: item.uuid,
			name: item.name,
			x: item.x,
			y: item.y,
			width: item.width,
			height: item.height,
			itemType: item.itemType,
			graphType: item.graphType,
			metric: item.metric ? {
				uuid: item.metric.uuid,
				compareMetric: item.metric.compareMetric ?? false,
				name: '',
				code: '',
				filters: [],
			} : undefined,
			itemProperty: {
				...(item.itemProperty ?? {} as IDashlyDashboardItemPropertyModel),
				drillthrough: item.itemProperty?.drillthrough ?? false,
				drilldown: item.itemProperty?.drilldown ?? false,
			},
			itemContent: item.itemContent,
			icon: item.icon ?? '',
			iconColor: item.iconColor ?? '',
			limits: [],
			sequence: 0,
			placeholderUuid: '',
			formattedValue: false,
			chartLine: false,
			drillthrough: false,
			active: true,
			deleted: false,
		})),
		filters: (preview.filters ?? []).map(mapPreviewFilterToDashlyFilter),
		icon: '',
		description: '',
		sequence: 0,
		requiredPermission: '',
		favourite: false,
		active: true,
		customer: undefined,
		updateThumbnail: false,
	} as IDashlyDashboardModel;
}

export class DashboardService extends BaseService<IDashboardModel, ICrudDashboardModel> {

	public async fetchMenuItems(): Promise<AxiosResponse<ApiResponse<IDashboardMenuItemModel[]>>> {
		return await api.get(`v1/${this.modelUrl()}/menu-items`, {});
	}

	/**
	 * @deprecated The method should not be used and is succeeded by dashboard viewer page
	 */
	public async fetchSpoofyMenuItems(): Promise<AxiosResponse<ApiResponse<ISpoofyDashboardMenuItemModel[]>>> {
		return await api.get(`v1/${this.modelUrl()}/menu-items/spoofy`, {});
	}

	public async fetchDashboardViewerItems(filterData?: any): Promise<AxiosResponse<ApiResponse<Page<IDashboardViewerItemModel[]>>>> {
		return await api.get(`v1/${this.modelUrl()}/dashboard-viewer`, {
			params: filterData
		});
	}

	public async fetchDashboard(uuid: string, params?: any): Promise<AxiosResponse<ApiResponse<IDashlyDashboardModel>>> {
		return await api.get(`v1/${this.modelUrl()}/${uuid}/dashboarding`, {
			params: params
		});
	}

	public async fetchExperimentalDashboard(uuid: string, params?: any): Promise<AxiosResponse<ApiResponse<IDashlyDashboardModel>>> {
		return await api.get(`v1/${this.modelUrl()}/${uuid}/dashboarding/experimental`, {
			params: params
		});
	}

	public async fetchLayoutPreview(uuid: string): Promise<AxiosResponse<ApiResponse<IDashboardLayoutPreviewModel>>> {
		return await api.get(`v1/${this.modelUrl()}/${uuid}/dashboarding/layout-preview`);
	}

	public async fetchDashboardFilters(uuid: string, params?: any): Promise<AxiosResponse<ApiResponse<IDashlyFilterModel[]>>> {
		return await api.get(`v1/${this.modelUrl()}/${uuid}/dashboarding-filters`, {
			params: params,
		});
	}

	public async getVisualGroupOptions(customerUuid: string): Promise<string[]> {
		const response: AxiosResponse<ApiResponse<{ options?: Array<{ value: string }> }>> =
			await api.get(`v1/${this.modelUrl()}/visual-group-options`, {
				params: { customer: customerUuid },
			});
		return (response.data.data?.options ?? []).map((option) => option.value);
	}

	public async retrieveDashboardDefinition(uuid: string, chartFilter: any): Promise<AxiosResponse<ApiResponse<any>>> {
		return await api.post(`v1/${this.modelUrl()}/${uuid}/definition`, {params: chartFilter});
	}

	public async updateFilterOptions(uuid: string, filterOptionModels?: IDashlyFilterModel[]): Promise<AxiosResponse<ApiResponse<IDashlyFilterModel[]>>> {
		return await api.put(`v1/${this.modelUrl()}/${uuid}/filter-option`, filterOptionModels);
	}

	public async putDashboard(uuid: string, dashboard: any): Promise<AxiosResponse<ApiResponse<IDashlyDashboardModel>>> {
		return await api.put(`v1/${this.modelUrl()}/${uuid}/dashboarding`, dashboard);
	}

	public async putExperimentalDashboard(uuid: string, dashboard: any): Promise<AxiosResponse<ApiResponse<IDashlyDashboardModel>>> {
		return await api.put(`v1/${this.modelUrl()}/${uuid}/dashboarding/experimental`, dashboard);
	}

	public async fetchDashboardThumbnail(uuid: string): Promise<AxiosResponse<ApiResponse<IDashboardThumbnailModel>>> {
		return await api.get(`v1/${this.modelUrl()}/${uuid}/thumbnail`);
	}

	public async postDashboardThumbnail(uuid: string, image: any): Promise<AxiosResponse<ApiResponse<IDashlyDashboardModel>>> {
		return await api.post(`v1/${this.modelUrl()}/${uuid}/thumbnail`, { thumbnail: image } as IDashboardThumbnailModel);
	}

	public async cloneDashboard(uuid: string, dashboard: any): Promise<AxiosResponse<ApiResponse<any>>> {
		return await api.post(`v1/${this.modelUrl()}/${uuid}/clone`, dashboard);
	}

	public async retrieveFilterOptions(metricUuids: string[]): Promise<AxiosResponse<ApiResponse<IDashlyDashboardFilterOptionModel[]>>> {
		return await api.post(`v1/${this.modelUrl()}/filter-option/retrieve`, metricUuids);
	}

	public async retrieveSubDashboardUuidForDashboardItem(dashboardUuid: string, dashboardItemUuid: string): Promise<AxiosResponse<ApiResponse<string>>> {
		return await api.post(`v1/${this.modelUrl()}/${dashboardUuid}/sub/${dashboardItemUuid}`);
	}

	protected modelUrl(): string {
		return "dashboard";
	}

}